/*
 * @Author: Alex
 * @LastEditors: Alex yxfacw@163.com
 * @Date: 2023-05-25 13:18:16
 * @Description:  地图通透镜tool
 * Move around the map to see the effect. Use the ↑ up and ↓ down arrow keys to adjust the spyglass size
 */

import { BaseMapTool } from './mapToolBase'
import { Map } from 'ol'
import { Layer } from 'ol/layer'
import { Pixel } from 'ol/pixel'
import { unByKey } from 'ol/Observable'
import { EventsKey } from 'ol/events'
import { getRenderPixel } from 'ol/render'

interface MapSpyEvent {
  [_: string]: any
}
export default class MapSpyTool extends BaseMapTool<MapSpyEvent> {
  /**
   * 默认通透镜的半径
   */
  private _radius = 75

  // get the pixel position with every move
  private _mousePosition: Pixel | null = null

  private _mapContainer: HTMLElement

  /**
   * 进行 spy 的目标图层
   */
  private _targetLayer: Layer

  private _prerenderListener: EventsKey | null = null

  private _postrenderListener: EventsKey | null = null

  /**
   * true： 自动添加图层到 map 的 top layer
   */
  private _autoAdd2Top = false

  constructor(options: {
    map: Map
    target: Layer
    radius?: number
    autoAdd2Top?: boolean
  }) {
    super(options.map)
    this._radius = options.radius ?? 75
    this._mapContainer = options.map.getTargetElement()
    this._targetLayer = options.target
    this._autoAdd2Top = options.autoAdd2Top ?? true
  }

  protected startInternal(): void {
    this._prerenderListener = this._targetLayer.on('prerender', event => {
      const ctx = event.context as CanvasRenderingContext2D
      if (ctx) {
        ctx.save()
        ctx.beginPath()
        if (this._mousePosition) {
          // only show a circle around the mouse
          const pixel = getRenderPixel(event, this._mousePosition)
          const offset = getRenderPixel(event, [
            this._mousePosition[0] + this._radius,
            this._mousePosition[1]
          ])
          const canvasRadius = Math.sqrt(
            Math.pow(offset[0] - pixel[0], 2) +
              Math.pow(offset[1] - pixel[1], 2)
          )
          ctx.arc(pixel[0], pixel[1], canvasRadius, 0, 2 * Math.PI)
          ctx.lineWidth = (5 * canvasRadius) / this._radius
          ctx.strokeStyle = 'rgba(0,0,0,0.5)'
          ctx.stroke()
        }
        ctx.clip()
      }
    })

    // after rendering the layer, restore the canvas context
    this._postrenderListener = this._targetLayer.on(
      'postrender',
      function (event) {
        const ctx = event.context as CanvasRenderingContext2D
        ctx.restore()
      }
    )
    document.addEventListener('keydown', evt => this.resizeRadius(evt))
    this._mapContainer.addEventListener('mousemove', evt => this.mapMove(evt))
    this._mapContainer.addEventListener('mouseout', evt => this.mapOut(evt))
    if (this._autoAdd2Top) {
      // 这里将图层移动到最上层 否则无法显示
      this.map.removeLayer(this._targetLayer)
      this.map.addLayer(this._targetLayer)
      this._targetLayer.setVisible(true)
    }
  }
  protected stopInternal(): void {
    document.removeEventListener('keydown', this.resizeRadius)
    this._mapContainer.removeEventListener('mousemove', this.mapMove)
    this._mapContainer.removeEventListener('mouseout', this.mapOut)
    if (this._prerenderListener) {
      unByKey(this._prerenderListener)
    }
    if (this._postrenderListener) {
      unByKey(this._postrenderListener)
    }
    if (this._autoAdd2Top) {
      this._targetLayer.setVisible(false)
      this.map.removeLayer(this._targetLayer)
    }
  }

  private resizeRadius(evt: KeyboardEvent) {
    if (evt.key === 'ArrowUp') {
      this._radius = Math.min(this._radius + 5, 150)
      this.map.render()
      evt.preventDefault()
    } else if (evt.key === 'ArrowDown') {
      this._radius = Math.max(this._radius - 5, 25)
      this.map.render()
      evt.preventDefault()
    }
  }

  private mapMove(evt: MouseEvent) {
    this._mousePosition = this.map.getEventPixel(evt)
    this.map.render()
  }

  private mapOut(evt: MouseEvent) {
    this._mousePosition = null
    this.map.render()
  }
}
